/* eslint-disable */
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Svg, {Circle, Path} from 'react-native-svg';
import {launchImageLibrary} from 'react-native-image-picker';
import {useState} from 'react';
import Toast from 'react-native-toast-message';
import {useTranslation} from 'react-i18next';
import AuthHeader from '../../components/AuthHeader';
import AuthInput from '../../components/AuthInput';
import PrimaryButton from '../../components/PrimaryButton';
import {
  sendFeedback,
  SUBJECT_MAX,
  MESSAGE_MAX,
} from '../../services/feedback-service';
import {colors, card, fonts, radii, spacing, typography} from '../../config/theme';

// The counter turns red this close to the limit.
const MESSAGE_WARN = 480;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

// 24px line glyphs, drawn inline so stroke colour follows the theme.
const Glyph = ({children, color, size = 20}) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    {children}
  </Svg>
);

const NoteGlyph = () => (
  <Glyph color={colors.textSubtle}>
    <Path d="M4 6h12M4 11h12M4 16h6M14 20l1-3 5-5 2 2-5 5-3 1z" />
  </Glyph>
);

const CameraGlyph = () => (
  <Glyph color={colors.primary} size={24}>
    <Path d="M3 8a2 2 0 0 1 2-2h2l2-2h6l2 2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z" />
    <Circle cx={12} cy={13} r={3.5} />
  </Glyph>
);

const UploadGlyph = () => (
  <Glyph color={colors.textStrong} size={16}>
    <Path d="M7 18a5 5 0 0 1-.5-9.98A6 6 0 0 1 18 9a4.5 4.5 0 0 1-.5 9M12 12v8M9 15l3-3 3 3" />
  </Glyph>
);

const CloseGlyph = () => (
  <Glyph color={colors.textMuted} size={18}>
    <Path d="M6 6l12 12M18 6L6 18" />
  </Glyph>
);

const formatSize = bytes =>
  bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : '';

// Send Feedback (Stitch "Pompi - Feedback"): subject, message and an
// optional photo, posted to the backend's feedback endpoint.
const Feedback = ({navigation}) => {
  const {t} = useTranslation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  // {uri, type, name, size} of the attached photo, or null
  const [photo, setPhoto] = useState(null);
  const [messageFocused, setMessageFocused] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const handlePickPhoto = async () => {
    try {
      const res = await launchImageLibrary({
        mediaType: 'photo',
        selectionLimit: 1,
        quality: 0.8,
        maxWidth: 2048,
        maxHeight: 2048,
      });
      if (res.didCancel) {
        return; // the user closed the gallery
      }
      if (res.errorCode) {
        Toast.show({
          type: 'error',
          text1:
            res.errorMessage ||
            t('root.Toast.Error.PhotoFailed', 'Could not open your photos'),
        });
        return;
      }
      const asset = res.assets?.[0];
      if (!asset?.uri) {
        return;
      }
      if (asset.fileSize && asset.fileSize > MAX_PHOTO_BYTES) {
        Toast.show({
          type: 'error',
          text1: t('root.Feedback.photoTooLarge'),
        });
        return;
      }
      setPhoto({
        uri: asset.uri,
        type: asset.type || 'image/jpeg',
        name: asset.fileName || `feedback_${Date.now()}.jpg`,
        size: asset.fileSize,
      });
    } catch (err) {
      console.log('pick photo error', err);
      Toast.show({
        type: 'error',
        text1: t('root.Toast.Error.PhotoFailed', 'Could not open your photos'),
      });
    }
  };

  const handleSubmit = async () => {
    if (sending) {
      return;
    }
    const nextErrors = {};
    if (!subject.trim()) {
      nextErrors.subject = t('root.Feedback.subjectRequired');
    }
    if (!message.trim()) {
      nextErrors.message = t('root.Feedback.messageRequired');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      return;
    }

    setSending(true);
    const result = await sendFeedback({
      subject: subject.trim(),
      message: message.trim(),
      file: photo,
    });
    setSending(false);

    if (!result.success) {
      // 400: the server names the fields it rejected.
      if (result.fieldErrors?.subject || result.fieldErrors?.message) {
        setErrors({
          subject: result.fieldErrors.subject,
          message: result.fieldErrors.message,
        });
      }
      Toast.show({
        type: 'error',
        text1: t('root.Feedback.failed'),
        text2: result.tooMany ? t('root.Feedback.tooMany') : result.error,
      });
      return;
    }
    Toast.show({
      type: 'success',
      text1: t('root.Feedback.successTitle'),
      text2: t('root.Feedback.successBody'),
    });
    navigation.goBack();
  };

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t('root.Feedback.title')}
        onBack={() => navigation.goBack()}
      />
      <KeyboardAvoidingView
        style={styles.body}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <AuthInput
            label={t('root.Feedback.subject')}
            required
            placeholder={t('root.Feedback.subjectPlaceholder')}
            value={subject}
            onChangeText={value => {
              setSubject(value);
              setErrors(prev => ({...prev, subject: undefined}));
            }}
            maxLength={SUBJECT_MAX}
            returnKeyType="next"
            icon={<NoteGlyph />}
            error={errors.subject}
          />

          <View>
            <View style={styles.labelRow}>
              <Text style={styles.label}>
                {t('root.Feedback.message')}
                <Text style={styles.required}> *</Text>
              </Text>
              <Text
                style={[
                  styles.counter,
                  message.length >= MESSAGE_WARN && styles.counterWarn,
                ]}>
                {message.length} / {MESSAGE_MAX}
              </Text>
            </View>
            <View
              style={[
                styles.messageBox,
                messageFocused && styles.messageBoxFocused,
                errors.message && styles.messageBoxError,
              ]}>
              <TextInput
                style={styles.messageInput}
                value={message}
                onChangeText={value => {
                  setMessage(value);
                  setErrors(prev => ({...prev, message: undefined}));
                }}
                placeholder={t('root.Feedback.messagePlaceholder')}
                placeholderTextColor={colors.textMuted}
                selectionColor={colors.secondary}
                accessibilityLabel={t('root.Feedback.message')}
                maxLength={MESSAGE_MAX}
                multiline
                textAlignVertical="top"
                onFocus={() => setMessageFocused(true)}
                onBlur={() => setMessageFocused(false)}
              />
            </View>
            {errors.message ? (
              <Text style={styles.error} accessibilityLiveRegion="polite">
                {errors.message}
              </Text>
            ) : null}
          </View>

          <View style={styles.attachSection}>
            <View>
              <Text style={styles.label}>
                {t('root.Feedback.attach')}
                <Text style={styles.optional}>
                  {' '}
                  {t('root.Feedback.optional')}
                </Text>
              </Text>
              <Text style={styles.hint}>{t('root.Feedback.attachHint')}</Text>
            </View>

            {photo ? (
              <View style={[styles.card, styles.attachment]}>
                <Image source={{uri: photo.uri}} style={styles.thumb} />
                <View style={styles.attachmentText}>
                  <Text style={styles.fileName} numberOfLines={1}>
                    {photo.name}
                  </Text>
                  <View style={styles.metaRow}>
                    {photo.size ? (
                      <>
                        <Text style={styles.fileSize}>
                          {formatSize(photo.size)}
                        </Text>
                        <View style={styles.metaDot} />
                      </>
                    ) : null}
                    <Text style={styles.ready}>{t('root.Feedback.ready')}</Text>
                  </View>
                </View>
                <Pressable
                  onPress={() => setPhoto(null)}
                  hitSlop={8}
                  accessibilityRole="button"
                  accessibilityLabel={t('root.Feedback.removePhoto')}
                  style={({pressed}) => [
                    styles.removeButton,
                    pressed && styles.pressed,
                  ]}>
                  <CloseGlyph />
                </Pressable>
              </View>
            ) : (
              <Pressable
                onPress={handlePickPhoto}
                accessibilityRole="button"
                style={({pressed}) => [
                  styles.card,
                  styles.dropZone,
                  pressed && styles.dropZonePressed,
                ]}>
                <View style={styles.cameraCircle}>
                  <CameraGlyph />
                </View>
                <Text style={styles.dropTitle}>
                  {t('root.Feedback.uploadTitle')}
                </Text>
                <Text style={styles.dropSubtitle}>
                  {t('root.Feedback.uploadSubtitle')}
                </Text>
                <View style={styles.selectChip}>
                  <UploadGlyph />
                  <Text style={styles.selectChipText}>
                    {t('root.Feedback.selectFile')}
                  </Text>
                </View>
              </Pressable>
            )}
          </View>

          <PrimaryButton
            title={t('root.Feedback.submit')}
            onPress={handleSubmit}
            loading={sending}
            style={styles.submit}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  body: {
    flex: 1,
  },
  content: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xxl,
    gap: spacing.lg,
  },
  card: {
    ...card,
    borderRadius: radii.sm,
  },
  pressed: {
    opacity: 0.7,
  },

  // Labels (same look as AuthInput's label)
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    fontFamily: fonts.semiBold,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textStrong,
    marginBottom: 6,
  },
  required: {
    color: colors.brandBright,
  },
  optional: {
    fontFamily: fonts.regular,
    color: colors.textMuted,
  },
  hint: {
    ...typography.bodySm,
    color: colors.textMuted,
  },
  counter: {
    fontFamily: fonts.medium,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textSubtle,
    marginBottom: 6,
  },
  counterWarn: {
    color: colors.error,
  },
  error: {
    fontFamily: fonts.medium,
    fontSize: 12,
    lineHeight: 16,
    color: colors.error,
    marginTop: 6,
  },

  // Message box (multiline twin of AuthInput's outline field)
  messageBox: {
    minHeight: 132,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  // The 2pt border would nudge the text, so trim the padding to match.
  messageBoxFocused: {
    borderWidth: 2,
    borderColor: colors.primary,
    paddingHorizontal: 15,
    paddingVertical: 11,
  },
  messageBoxError: {
    borderColor: colors.error,
    backgroundColor: colors.errorTint,
  },
  messageInput: {
    flex: 1,
    minHeight: 108,
    padding: 0,
    fontFamily: fonts.medium,
    fontSize: 14,
    lineHeight: 20,
    color: colors.textStrong,
  },

  // Photo
  attachSection: {
    gap: spacing.sm,
  },
  dropZone: {
    alignItems: 'center',
    padding: spacing.lg,
  },
  dropZonePressed: {
    backgroundColor: colors.surfaceMuted,
    transform: [{scale: 0.99}],
  },
  cameraCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  dropTitle: {
    ...typography.labelLg,
    letterSpacing: 0,
    color: colors.textStrong,
    textAlign: 'center',
  },
  dropSubtitle: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: 2,
  },
  selectChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 14,
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
  },
  selectChipText: {
    ...typography.labelMd,
    letterSpacing: 0,
    color: colors.textStrong,
  },
  attachment: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.md,
  },
  thumb: {
    width: 48,
    height: 48,
    borderRadius: radii.badge,
    backgroundColor: colors.surfaceMuted,
  },
  attachmentText: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    ...typography.labelLg,
    letterSpacing: 0,
    color: colors.textStrong,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: 2,
  },
  fileSize: {
    ...typography.bodySm,
    color: colors.textMuted,
  },
  metaDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.textSubtle,
  },
  ready: {
    ...typography.labelMd,
    letterSpacing: 0,
    color: colors.brandBright,
  },
  removeButton: {
    width: 32,
    height: 32,
    borderRadius: radii.badge,
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },

  submit: {
    marginTop: spacing.sm,
  },
});

export default Feedback;
